import styled from '@emotion/styled'
import { css } from '@emotion/react'
import { theme } from 'styles/theme/hav-theme'
import { TextAlignOptions } from './Table'
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined'
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined'
import { Link } from 'react-router-dom'

type TableWrapperProps = {
  maxHeight: number | string
  notScrollable?: boolean
  hasFixedCols?: boolean
  isPrinting?: boolean
}

type TableGroupProps = {
  checked?: boolean
}

type RowItemProps = {
  dense: boolean
  hasCheckbox?: boolean
  cursorPointer?: boolean
  distributeWidth: boolean
  fixed?: boolean
  fixedLeft?: boolean
  fixedLeftWidth?: number
  fixedLeftOffset?: number
  fixedAction?: boolean
  actionWidth?: number
  actionOffset?: number
  rowHeight: number | string
  isIcon?: boolean
  textAlign?: TextAlignOptions
  inputAlign?: TextAlignOptions
  isExpanded?: boolean
}

type SubRowItemProps = {
  rowHeight: number | string
  isIcon?: boolean
  textAlign?: TextAlignOptions
  fixed?: boolean
  fixedLeft?: boolean
  fixedLeftWidth?: number
  fixedLeftOffset?: number
}

type TableHeadProps = {
  fixed: boolean
}

type HeadItemProps = {
  dense: boolean
  fixed?: boolean
  fixedLeft?: boolean
  fixedLeftWidth?: number
  fixedLeftOffset?: number
  fixedAction?: boolean
  actionWidth?: number
  actionOffset?: number
  isIcon?: boolean
  textAlign?: TextAlignOptions
}

type RowProps = {
  expanded?: boolean
}

type DividerProps = {
  expanded?: boolean
  rowHeight: number
}

type ActionDividerProps = {
  offset: number
}

type ActionWrapperProps = {
  fixed?: boolean
}

type SubRowProps = {
  visible: boolean
}

type IconProps = {
  customcolor: string
}

export const Wrapper = styled.div<TableWrapperProps>`
  width: 100%;
  ${({ notScrollable }) => !notScrollable && `overflow-x: auto;`}
  ${({ hasFixedCols }) => hasFixedCols && `overflow-x: scroll;`}

  background: ${theme.colors.neutral.white};

  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.neutral.light};
  }

  max-height: ${({ maxHeight }) =>
    typeof maxHeight === 'string' ? maxHeight : `${maxHeight}px`};
`

export const WrapperTable = styled.div`
  width: 100%;
  display: inherit;
`

export const ActionTableWrapper = styled.div<{ isPrinting: boolean }>`
  position: relative;
  display: ${({ isPrinting }) => (isPrinting ? 'inline-table' : 'grid')};
  width: 100%;
`

export const Table = styled.table`
  width: 100%;

  thead {
    td {
      border-bottom: 1px solid ${theme.colors.neutral.lighthover};
    }
  }

  tbody:first-of-type tr:first-of-type td {
    padding-top: 7px;
  }
`

export const TableHead = styled.thead<TableHeadProps>`
  background-color: ${theme.colors.neutral.white};

  ${({ fixed }) =>
    fixed &&
    css`
      top: 0;
      z-index: 10;
      position: sticky;
      position: -webkit-sticky;
      background-color: ${theme.colors.neutral.white};
    `}
`

export const HeadRow = styled.tr`
  background-color: inherit;
`

export const CheckboxWrapper = styled.td<ActionWrapperProps>`
  padding-left: ${theme.spacing.sm};

  ${({ fixed }) =>
    fixed &&
    css`
      left: 0;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
    `}
`

export const RowCheckboxWrapper = styled.td<ActionWrapperProps>`
  padding-left: ${theme.spacing.sm};

  ${({ fixed }) =>
    fixed &&
    css`
      left: 0;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
    `}
`

export const ExpandActionWrapper = styled.td<ActionWrapperProps>`
  position: relative;
  padding: 0;

  ${({ fixed }) =>
    fixed &&
    css`
      left: 0;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
    `}
`

export const RowGroup = styled.tbody<TableGroupProps>`
  background-color: ${theme.colors.neutral.white};

  :nth-of-type(odd) {
    background-color: ${theme.colors.neutral[100]};
  }

  ${({ checked }) =>
    checked && `background-color: ${theme.colors.quartenary} !important;`}

  :hover {
    background-color: ${theme.colors.quartenary};
  }

  tr:first-of-type td {
    :first-of-type {
      border-top-left-radius: ${theme.shapes.borderRadius.md};
    }
    :last-of-type {
      border-top-right-radius: ${theme.shapes.borderRadius.md};
    }
  }
  tr:last-of-type td {
    :first-of-type {
      border-bottom-left-radius: ${theme.shapes.borderRadius.md};
    }
    :last-of-type {
      border-bottom-right-radius: ${theme.shapes.borderRadius.md};
    }
  }
`

export const ExpandButton = styled.button`
  position: relative;
  width: 25px;
  display: flex;
  justify-content: center;
  padding: 0;
  border: none;
  outline: none;
  background: none;
  cursor: pointer;

  :disabled {
    cursor: wait;
  }
`

export const ExpandIcon = styled(AddBoxOutlinedIcon)<IconProps>`
  color: ${({ customcolor }) => customcolor};
  font-size: ${theme.typography.sizes.sm};
`

export const CollapseIcon = styled(IndeterminateCheckBoxOutlinedIcon)<IconProps>`
  color: ${({ customcolor }) => customcolor};
  font-size: ${theme.typography.sizes.sm};
`

export const HeadItem = styled.td<HeadItemProps>`
  white-space: nowrap;
  padding: ${({ dense }) =>
    dense ? `${theme.spacing.xs}` : `${theme.spacing.sm} ${theme.spacing.md}`};

  color: ${theme.colors.neutral.black};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weight.lg};
  font-family: ${theme.typography.family.bold};
  text-align: ${({ textAlign }) => textAlign ?? 'left'};

  :last-of-type {
    padding: ${({ dense }) => (dense ? `${theme.spacing.xs}` : `${theme.spacing.sm}`)};
  }

  ${({ isIcon }) =>
    isIcon &&
    css`
      padding: 0;
    `}

  ${({ fixed }) =>
    fixed &&
    css`
      left: 0;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
    `}

    ${({ fixedLeft, fixedLeftOffset, fixedLeftWidth }) =>
    fixedLeft &&
    css`
      left: ${fixedLeftOffset}px;
      min-width: ${fixedLeftWidth}px;
      max-width: ${fixedLeftWidth}px;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
    `}

  ${({ fixedAction, actionOffset, actionWidth }) =>
    fixedAction &&
    css`
      right: ${actionOffset}px;
      min-width: ${actionWidth}px;
      max-width: ${actionWidth}px;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`

export const ExpandLineStart = styled.div<DividerProps>`
  position: absolute;
  top: 14px;
  height: ${({ expanded, rowHeight }) => (expanded ? `${rowHeight / 2}px` : '0')};
  width: 1px;
  background-color: ${theme.colors.neutral.mediumhover};
  transition: height 0.1s;
`

export const ExpandLine = styled.div<DividerProps>`
  display: flex;
  justify-content: center;

  div {
    height: ${({ rowHeight }) => `${rowHeight}px`};
    width: 1px;
    background-color: ${theme.colors.neutral.mediumhover};
  }
`

export const ExpandLineEnd = styled.div<DividerProps>`
  display: flex;
  justify-content: flex;
  height: ${({ rowHeight }) => `${rowHeight}px`};
  overflow: hidden;
  position: relative;

  div {
    width: 24px;
    height: ${({ rowHeight }) => `${rowHeight}px`};
    border: 1px solid #bcbcbc;
    border-radius: 6px;
    position: absolute;
    left: 12px;
    bottom: ${({ rowHeight }) => `${rowHeight / 2}px`};
  }
`

export const TableRow = styled.tr<RowProps>`
  background-color: inherit;

  td {
    :first-of-type {
      border-bottom-left-radius: ${({ expanded }) =>
        expanded ? 0 : theme.shapes.borderRadius.md};
    }
    :last-of-type {
      border-bottom-right-radius: ${({ expanded }) =>
        expanded ? 0 : theme.shapes.borderRadius.md};
    }
  }
`

export const SubRow = styled.tr<SubRowProps>`
  visibility: ${({ visible }) => (visible ? 'visible' : 'collapse')};
  transition: visibility 0.1s;
  background-color: inherit;
`

export const RowItem = styled.td<RowItemProps>`
  height: ${({ rowHeight }) => `${rowHeight}px`};
  padding: ${({ dense }) => (dense ? `0 ${theme.spacing.xs}` : `0 ${theme.spacing.md}`)};
  ${({ cursorPointer }) => cursorPointer && `cursor: pointer;`}
  white-space: nowrap;
  color: ${theme.colors.neutral.high};
  font-size: ${theme.typography.sizes.xs};
  ${({ valign }) => valign && `vertical-align: ${valign};`}
  text-align: ${({ textAlign }) => textAlign ?? 'left'};
  .MuiInputBase-input {
    text-align: ${({ inputAlign }) => inputAlign ?? 'center'};
  }

  :has(div.table-cell-success-bg) {
    background-color: ${`${theme.colors.alert.success.main}29`};
  }

  :first-of-type {
    ${({ hasCheckbox, isIcon }) =>
      !hasCheckbox && !isIcon && `padding-left: ${theme.spacing.xm};`}
  }
  :last-of-type {
    padding: ${({ dense }) =>
      dense ? `0 ${theme.spacing.xs}` : `0 ${theme.spacing.sm}`};

    width: ${({ distributeWidth }) => (distributeWidth ? 'auto' : '100%')};
  }

  ${({ isIcon }) =>
    isIcon &&
    css`
      padding: 0;
    `}

  ${({ fixed }) =>
    fixed &&
    css`
      left: 0;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
    `}

    ${({ fixedLeft, fixedLeftOffset, fixedLeftWidth }) =>
    fixedLeft &&
    css`
      left: ${fixedLeftOffset}px;
      min-width: ${fixedLeftWidth}px;
      max-width: ${fixedLeftWidth}px;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      box-sizing: border-box;
      background-color: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
    `}

  ${({ fixedAction, actionOffset, actionWidth }) =>
    fixedAction &&
    css`
      right: ${actionOffset}px;
      min-width: ${actionWidth}px;
      max-width: ${actionWidth}px;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`

export const TableLink = styled(Link)`
  display: flex;
  align-items: center;
  height: 100%;
  text-decoration: none;
  color: inherit;
  user-select: text;
`

export const SubRowItem = styled.td<SubRowItemProps>`
  white-space: nowrap;
  padding: 0 ${theme.spacing.md};
  color: ${theme.colors.neutral.high};
  font-size: ${theme.typography.sizes.xs};
  text-align: ${({ textAlign }) => textAlign ?? 'left'};

  :last-of-type {
    padding: 0 ${theme.spacing.sm};
  }

  ${({ isIcon }) =>
    isIcon &&
    css`
      padding: 0;
    `}

  ${({ fixed }) =>
    fixed &&
    css`
      left: 0;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      background-color: inherit;
    `}

    ${({ fixedLeft, fixedLeftOffset, fixedLeftWidth }) =>
    fixedLeft &&
    css`
      left: ${fixedLeftOffset}px;
      min-width: ${fixedLeftWidth}px;
      max-width: ${fixedLeftWidth}px;
      z-index: 5;
      position: sticky;
      position: -webkit-sticky;
      box-sizing: border-box;
      background-color: inherit;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`

export const ActionDivider = styled.div<ActionDividerProps>`
  position: absolute;
  height: calc(100% - 28px);
  width: 30px;
  top: 5px;
  opacity: 0.7;
  box-shadow: inset -10px 0px 9px rgba(0, 0, 0, 0.07);
  background: linear-gradient(90deg, rgba(231, 231, 231, 0) 38.33%, #e7e7e7 151.67%);

  ${({ offset }) =>
    offset &&
    css`
      right: ${offset}px;
    `}
`

export const FixedDividerLeft = styled.div<ActionDividerProps>`
  position: absolute;
  height: calc(100% - 28px);
  width: 30px;
  top: 5px;
  opacity: 0.7;
  box-shadow: inset -10px 0px 9px rgba(0, 0, 0, 0.07);
  background: linear-gradient(90deg, rgba(231, 231, 231, 0) 38.33%, #e7e7e7 151.67%);
  transform: scale(-1);

  ${({ offset }) =>
    offset &&
    css`
      left: ${offset}px;
    `}
`

export const ActionsLabel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`
